import { useWritersRoom, WritersRoomPhase } from '@/hooks/useWritersRoom';
import { Scene } from '@/lib/types';
import { AutoAwesome, ChevronRight, PlayArrow, Refresh } from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WritersRoomPipelineList } from './WritersRoomPipelineList';
import { WritersRoomRedoDialog } from './WritersRoomRedoDialog';
import { WritersRoomValidationIssues } from './WritersRoomValidationIssues';

const DRAWER_WIDTH = 380;

interface WritersRoomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  scene: Scene | null;
  novelId: string | null;
  textId?: string;
}

export function WritersRoomDrawer({ isOpen, onClose, scene, novelId, textId }: WritersRoomDrawerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    phase,
    error,
    isSnapshotCreated,
    isProcessing,
    isTokenDepleted,
    validation,
    handlePrepare,
    handleExecute,
    removeValidationIssue,
  } = useWritersRoom(novelId, scene?.id ?? null, textId ?? null);

  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [redoPhase, setRedoPhase] = useState<WritersRoomPhase | null>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Guard: can't use this drawer without an active scene
  if (!scene) return null;

  const canContinue = phase === 'prepared' && !isProcessing;
  const isIdle = phase === 'idle';
  const isApproved = phase === 'approved' || phase === 'ready';

  const handleActionButton = () => {
    if (error) {
      isSnapshotCreated ? handleExecute(phase) : handlePrepare(scene);
      return;
    }
    if (isIdle) {
      handlePrepare(scene);
    } else if (phase === 'prepared') {
      handleExecute();
    } else if (isApproved) {
      setConfirmRegenerate(true);
    }
  };

  const handleConfirmRegenerate = () => {
    setConfirmRegenerate(false);
    handlePrepare(scene);
  };

  const handleConfirmRedo = () => {
    if (!redoPhase) return;
    redoPhase === 'snapshot'
      ? handlePrepare(scene, true)
      : handleExecute(redoPhase, true);
    setRedoPhase(null);
  };

  const actionButtonLabel = () => {
    if (isProcessing) return t(`writers_room.btn_${phase}`) || t('writers_room.btn_writing');
    if (error) return t('common.retry');
    if (canContinue) return t('common.continue');
    if (isApproved) return t('common.regenerate');
    return t('writers_room.btn_start');
  };

  const actionButtonIcon = isProcessing
    ? <CircularProgress size={20} color="inherit" />
    : canContinue
      ? <PlayArrow />
      : isApproved
        ? <Refresh />
        : <AutoAwesome />;

  const isActionDisabled =
    isProcessing ||
    isTokenDepleted ||
    (phase !== 'idle' && !error && phase !== 'prepared' && phase !== 'approved');

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        onClose={onClose}
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            width: isMobile ? '90vw' : DRAWER_WIDTH,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            boxShadow: 0,
          },
        }}
        sx={{
          width: isOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: isMobile ? '90vw' : DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: isMobile ? undefined : 'relative',
            borderLeft: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesome sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: 'text.secondary' }}>
                {t('writers_room.title')}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size="small">
              <ChevronRight />
            </IconButton>
          </Box>

          {/* ── Scrollable Body ── */}
          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>

            <WritersRoomPipelineList
              phase={phase}
              isSnapshotCreated={isSnapshotCreated}
              error={error}
              onRedo={setRedoPhase}
            />

            {/* Error banner */}
            {error && (
              <Box sx={{ p: 1.5, bgcolor: 'error.main', color: 'error.contrastText', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={700}>
                  {t('common.error').toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('writers_room.pipeline_error')}
                </Typography>
              </Box>
            )}

            {/* Context hint */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('writers_room.context_hint')} <strong>{t('writers_room.scene_description')}</strong> {t('writers_room.context_hint_and')} <strong>{t('writers_room.involved_entities')}</strong> {t('writers_room.context_hint_to')}
              </Typography>
            </Box>

            <WritersRoomValidationIssues
              phase={phase}
              validation={validation}
              onRemoveIssue={removeValidationIssue}
            />
          </Box>

          {/* ── Sticky Footer ── */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            {confirmRegenerate ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="warning.main" fontWeight={700}>
                  {t('writers_room.confirm_regenerate')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button fullWidth size="small" variant="outlined" onClick={() => setConfirmRegenerate(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button fullWidth size="small" variant="contained" color="warning" onClick={handleConfirmRegenerate}>
                    {t('common.confirm')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Tooltip
                title={isTokenDepleted ? t('entity_image_manager.common.tokens_depleted_tooltip') : ''}
                placement="top"
              >
                {/* span required so Tooltip works on a disabled button */}
                <span style={{ width: '100%', display: 'block' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isActionDisabled}
                    startIcon={actionButtonIcon}
                    onClick={handleActionButton}
                    sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}
                  >
                    {actionButtonLabel()}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Drawer>

      <WritersRoomRedoDialog
        redoPhase={redoPhase}
        onClose={() => setRedoPhase(null)}
        onConfirm={handleConfirmRedo}
      />
    </>
  );
}
